// components/SearchPanel.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Filter, X, Calendar, Search } from "lucide-react";
import MultiSelect from "components/MultiSelect";
import CompanyPickerField from "components/CompanyPickerField";
import { useSearch } from "context/SearchContext";
import { SEARCH_CONFIG } from "config/searchConfig";

export default function SearchPanel({
  namespace,
  title,
  isExpanded = false,
  onClose,
  // Optional: array of field `key`s to hide from this instance of the drawer
  // (e.g. partner users shouldn't see Type/Tier/Account status filters).
  hiddenFields = [],
  // SOUND-618 ride-along: caller-injected option lists for fields declaring an
  // `optionsProp`, for vocabularies the SERVER owns and the client must not
  // hardcode (ticket request types come from GET /tickets/request-types).
  // Merged over the SearchContext-provided lists.
  extraOptions,
}) {
  const {
    filters, // applied (from URL)
    setAll, // write all to URL (one shot)
    reset, // reset URL to defaults
    campaignOptions,
    gatewayOptions,
  } = useSearch();

  // Local draft edited inside panel (decoupled from URL)
  const [draft, setDraft] = useState(filters);

  // Keep draft in sync when panel opens or when filters change while closed
  useEffect(() => {
    if (isExpanded) {
      setDraft(filters);
    } else {
      // when closed, keep draft fresh with latest applied filters
      setDraft(filters);
    }
  }, [isExpanded, filters]);

  // Close on Escape
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isExpanded, onClose]);

  const schema = SEARCH_CONFIG[namespace]?.fields || [];
  const optionsMap = useMemo(
    () => ({ campaignOptions, gatewayOptions, ...(extraOptions || {}) }),
    [campaignOptions, gatewayOptions, extraOptions]
  );
  const getOptions = (field) =>
    field.optionsProp ? optionsMap[field.optionsProp] || [] : [];

  // Options may be plain strings ("open") or {value|key, label} objects for
  // server-owned vocabularies. Returns [{value, label}] and DROPS anything
  // without a resolvable value: an <option> with value={undefined} silently
  // falls back to its text content, which submits a label as if it were a key.
  const normalizeOptions = (field) => {
    const raw = field.optionsProp ? getOptions(field) : field.options || [];
    return raw
      .map((opt) => {
        if (typeof opt === "string") {
          return {
            value: opt,
            label: opt.charAt(0).toUpperCase() + opt.slice(1).replace(/_/g, " "),
          };
        }
        const value = opt?.value ?? opt?.key;
        return value == null ? null : { value, label: opt.label ?? value };
      })
      .filter(Boolean);
  };

  const labelFor = (field) =>
    field.label ||
    field.key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

  // Active filter count (for header badge)
  const isActive = (field, src) => {
    const v = src[field.key];
    const d = field.default;
    switch (field.type) {
      case "multi":
        return (v?.length || 0) !== (d?.length || 0);
      case "boolean":
        return Boolean(v) !== Boolean(d);
      default:
        return (v ?? "") !== (d ?? "");
    }
  };
  const activeFilterCount = useMemo(
    () => schema.reduce((acc, f) => (isActive(f, draft) ? acc + 1 : acc), 0),
    [schema, draft]
  );

  // Field renderers (design-system inputs)
  const baseInput =
    "w-full bg-card text-foreground border border-border rounded-md px-3 py-2 " +
    "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand " +
    "transition";

  const renderDate = (field) => (
    <div className="space-y-2" key={field.key}>
      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        {labelFor(field)}
      </label>
      <input
        type="date"
        value={draft[field.key] || ""}
        onChange={(e) =>
          setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))
        }
        className={baseInput}
      />
    </div>
  );

  const renderNumber = (field) => (
    <label className="flex flex-col text-sm text-foreground" key={field.key}>
      <span className="font-medium">{labelFor(field)}</span>
      <input
        type="number"
        step={field.step ?? 1}
        min={field.min ?? undefined}
        max={field.max ?? undefined}
        className={`${baseInput} mt-1`}
        value={draft[field.key] ?? field.default ?? 0}
        onChange={(e) => {
          const val = e.target.value;
          const n = val === "" ? "" : Number(val);
          setDraft((prev) => ({ ...prev, [field.key]: val === "" ? "" : n }));
        }}
      />
    </label>
  );

  const renderCheckbox = (field) => (
    <label
      className="flex items-center gap-2 text-sm text-foreground"
      key={field.key}
    >
      <input
        type="checkbox"
        checked={!!draft[field.key]}
        onChange={() =>
          setDraft((prev) => ({ ...prev, [field.key]: !prev[field.key] }))
        }
        className="h-4 w-4 rounded border-border text-brand focus:ring-brand/40"
      />
      <span className="font-medium">{labelFor(field)}</span>
    </label>
  );

  const renderText = (field) => (
    <label className="flex flex-col text-sm text-foreground" key={field.key}>
      <span className="font-medium">{labelFor(field)}</span>
      <input
        type="text"
        className={`${baseInput} mt-1`}
        value={draft[field.key] || ""}
        onChange={(e) =>
          setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))
        }
        placeholder={field.placeholder || ""}
      />
    </label>
  );

  const renderSelect = (field) => (
    <label className="flex flex-col text-sm text-foreground" key={field.key}>
      <span className="font-medium">{labelFor(field)}</span>
      <select
        className={`${baseInput} mt-1`}
        value={draft[field.key] || ""}
        onChange={(e) =>
          setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))
        }
      >
        {/*
          The implicit blank entry means "no filter". A field whose default is
          NOT empty must omit it: write() deletes empty values from the URL and
          decode() falls back to the default, so selecting blank would silently
          revert to that default instead of clearing the filter.
        */}
        {!field.omitBlankOption && <option value="">All</option>}
        {normalizeOptions(field).map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );

  const renderMulti = (field) => (
    <div className="space-y-2 flex flex-col" key={field.key}>
      <label className="text-sm font-medium text-foreground">
        {labelFor(field)}
      </label>
      <MultiSelect
        options={getOptions(field)}
        selectedOptions={draft[field.key]}
        onChange={(vals) =>
          setDraft((prev) => ({ ...prev, [field.key]: vals }))
        }
        placeholder={field.placeholder || `Select ${labelFor(field)}`}
      />
    </div>
  );

  const renderCompanyPicker = (field) => (
    <CompanyPickerField
      key={field.key}
      label={labelFor(field)}
      companyType={field.pickerCompanyType}
      value={draft[field.key] || ""}
      valueLabel={draft[`${field.key}__label`] || ""}
      onChange={({ id, name }) =>
        setDraft((prev) => ({
          ...prev,
          [field.key]: id,
          // Side-channel label lives only in draft; URL persists the id.
          [`${field.key}__label`]: name,
        }))
      }
    />
  );

  const renderField = (field) => {
    const ui = field.ui || field.type;
    switch (ui) {
      case "date":
        return renderDate(field);
      case "number":
        return renderNumber(field);
      case "checkbox":
      case "boolean":
        return renderCheckbox(field);
      case "multi":
      case "multiselect":
        return renderMulti(field);
      case "select":
        return renderSelect(field);
      case "companyPicker":
        return renderCompanyPicker(field);
      case "string":
      default:
        return renderText(field);
    }
  };

  // Special-casing date range if both fields exist
  const renderedKeys = new Set();
  const hasDateRange =
    schema.some((f) => f.key === "startDate" && (f.ui || f.type) === "date") &&
    schema.some((f) => f.key === "endDate" && (f.ui || f.type) === "date");

  const panelTitle =
    title ||
    `${namespace?.charAt(0).toUpperCase()}${namespace?.slice(1)} Filters`;

  // Hidden when not expanded (trigger lives elsewhere)
  if (!isExpanded) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
        onClick={() => onClose?.()}
      />

      {/* Drawer */}
      <aside
        className="fixed top-0 right-0 h-full z-50 w-[420px] max-w-[90vw] bg-card text-card-foreground
                   border-l border-border shadow-2xl rounded-l-xl flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-panel-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <div className="grid h-8 w-8 place-items-center rounded-md bg-muted text-foreground/80">
              <Filter className="h-4 w-4" />
            </div>
            <h2
              id="search-panel-title"
              className="font-semibold text-foreground truncate"
            >
              {panelTitle}
            </h2>
            {activeFilterCount > 0 && (
              <span className="badge badge-brand shrink-0">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <button
            onClick={() => onClose?.()}
            className="btn btn-ghost h-8 w-8 p-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6 text-sm">
          {hasDateRange && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Date Range
              </label>
              <div className="grid grid-cols-2 gap-2">
                {["startDate", "endDate"].map((k) => {
                  renderedKeys.add(k);
                  return (
                    <input
                      key={k}
                      type="date"
                      value={draft[k] || ""}
                      onChange={(e) =>
                        setDraft((prev) => ({ ...prev, [k]: e.target.value }))
                      }
                      className={baseInput}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {schema.map((field) => {
            if (renderedKeys.has(field.key)) return null;
            // Caller can hide fields entirely (role/feature-gated filters).
            if (hiddenFields.includes(field.key)) return null;
            // Optional visibility gate based on other draft field values.
            if (typeof field.visibleWhen === "function" && !field.visibleWhen(draft)) {
              return null;
            }
            return <div key={field.key}>{renderField(field)}</div>;
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-5 space-y-3">
          <button
            onClick={() => {
              setAll(draft, { push: true }); // write once to URL
              onClose?.();
            }}
            className="btn btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            <Search className="h-4 w-4" />
            Apply Filters
          </button>

          <button
            onClick={() => {
              const defaults = Object.fromEntries(
                schema.map((f) => [f.key, f.default])
              );
              setDraft(defaults);
              reset({ push: true });
            }}
            className="btn btn-ghost w-full"
          >
            Reset
          </button>
        </div>
      </aside>
    </>
  );
}
